import { Component, HostListener, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Event, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { LegFiJwtService } from './services/auth/legfi-jwt.service';
import { JwtLegFiClaims } from './services/auth/jwt-legfi-claims.model';
import { GrowlerService } from './components/shared/growler/growler.service';
import { SvgIconService } from './services/svg-icon.service';
import { HostListenerService } from './services/host-listener.service';
import { GtmService } from './services/gtm.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import { BreakpointSizes } from './enums/breakpoint-sizes.enum';
import { IdleTimeoutService } from './services/idle-timeout.service';
import environment from 'environments/environment';
import { HubSpotService } from './services/hs.service';
import { AuthService } from './services/auth/auth.service';

@Component({
    selector: 'lf-root',
    templateUrl: './app.component.html',
})
export class AppComponent implements OnInit
{
    private mapsAPIKey: string = environment.GoogleMapsApiKey;
    private previousRoute: string;

    constructor(
            private _router: Router,
            private _svg: SvgIconService,
            private _host: HostListenerService,
            private _authService: AuthService,
            private _idle: IdleTimeoutService,
            private _gtm: GtmService,
            private _dialog: MatDialog,
            private _growler: GrowlerService,
            private _observer: BreakpointObserver,
            private _hsq: HubSpotService,
    ) {
        this._svg.registerAppMatIcons();
    }

    /**
     * Cheap "is the user signed in?" check for use inside templates.
     * Uses `raw()` (just a localStorage lookup) instead of `read()` —
     * the latter decodes the JWT and silently calls `clear()` if the
     * token is malformed, which we don't want firing on every CD cycle.
     */
    get isLoggedIn(): boolean {
        return !!LegFiJwtService.raw();
    }

    @HostListener('document:click', ['$event']) onAppClick(event: MouseEvent) {
        this._host.documentClickedEvent$.next(event);
    }

    @HostListener('window:keydown', ['$event']) onAppKeyDownEvent(event: KeyboardEvent) {
        this._host.windowKeydownEvent$.next(event);
    }

    @HostListener('window:keyup', ['$event']) onAppKeyUpEvent(event: KeyboardEvent) {
        this._host.windowKeyupEvent$.next(event);
    }

    @HostListener('window:scroll', ['$event']) onAppScrollEvent() {
        this._host.windowScrollEvent$.next();
    }

    @HostListener('document:visibilitychange', ['$event']) onAppVisibilityEvent() {
        if (document.visibilityState === 'visible') {
            this.handleTabVisibilityChange();
        }
    }

    ngOnInit() {
        if (LegFiJwtService.read() !== null) {
            this._idle.watch();
        }
        window.addEventListener('storage', (event) => {
            if (event.key === 'jwt-sync-trigger') {
                this.reloadForOrgSwitch();
            }
        });
        this.handleLoginRouteIssues();
        this.handleBreakpointObservers();

        // https://github.com/angular/components/tree/main/src/google-maps#readme
        const mapsScript = document.createElement('script');
        mapsScript.type = 'text/javascript';
        mapsScript.src = `https://maps.googleapis.com/maps/api/js?libraries=places&key=${this.mapsAPIKey}`;
        document.body.appendChild(mapsScript);
    }

    private handleLoginRouteIssues() {
        this.previousRoute = '/';
        this._router.events.subscribe((event: Event) => {
            if (event instanceof NavigationStart) {
                const url = event.url;

                if (url.indexOf('auth/login;nonce') > -1) {
                    const arr = url.split(';');
                    const nonce = arr[1].replace('nonce=', '');
                    const orgId = arr[2].replace('orgId=', '');
                    let uri = '';
                    if (arr.length > 3) {
                        uri = decodeURIComponent(arr[3].replace('uri=', ''));
                    }
                    this._router.navigate(['/auth/login/' + nonce + '/' + orgId + '/' + encodeURIComponent(uri)]);
                } else if (url.indexOf('auth/login;uri=/') > -1) {
                    const arr = url.split(';');
                    const uri = decodeURIComponent(arr[1].replace('uri=', ''));
                    this._router.navigate([
                        '/auth/login',
                        {uri: uri},
                    ]);
                } else if (url.indexOf('auth/login;uri=') > -1 && url.indexOf('sign-up') > -1) {
                    // TODO: determine true culprit and add exception
                    // On login fail from branded login page, app is redirecting to /auth/login;uri=%252Fauth%252Fsign-up%252ForgId
                    const arr = url.split('%252F');
                    this._router.navigate([
                        `/auth/sign-up/${arr[2]}`,
                    ]);
                }

                this._hsq.togglePixel(url);
            }

            if (event instanceof NavigationEnd) {
                this._gtm.pageView({
                    pageName: event.url,
                    previousRoute: this.previousRoute,
                });

                // Update to current route
                this.previousRoute = event.url;

                // close any open dialogs on route change
                this._dialog.closeAll();
            }
        });
    }

    /**
     * Another tab switched organization, so the JWT in shared localStorage now belongs to that
     * new org.
     *
     * SD-9967: reloading the CURRENT url re-requested a record belonging to the org the user
     * just left. Reads are org-scoped server-side so nothing crosses, but the tab is left on a
     * broken page: the new org's name over a record that is not theirs, with no data in it.
     *
     * Land on the new org's dashboard instead. An in-tab switch already does exactly this
     * (OrganizationDataService.switchOrganization), so this only brings the cross-tab path into
     * line with it.
     */
    private reloadForOrgSwitch(): void {
        const target = this.orgSwitchLandingRoute(LegFiJwtService.read(), window.location.pathname);

        if (target === null) {
            window.location.reload();
            return;
        }

        window.location.assign(target);
    }

    /**
     * Where a tab should land after another tab switched organization, or null to just reload.
     *
     * Takes the pathname rather than reading window.location so it stays a pure function and can
     * be tested directly; window.location is not configurable, so it cannot be spied on.
     * F-686d was a wrong branch here, so it is worth pinning.
     *
     * Only /app/ is redirected. The /admin/ and /pmc/ shells are signed-in surfaces too, and
     * they keep the plain reload: their pages are not organization-scoped the way /app/ is, and
     * an org dashboard is not a sensible landing for them. Deliberate limit, not an oversight.
     */
    private orgSwitchLandingRoute(jwt: JwtLegFiClaims | null, pathname: string): string | null {
        if (jwt === null || pathname.indexOf('/app/') === -1) {
            return null;
        }

        if (jwt.superUser && !jwt.impersonatedBy) {
            return '/app/organization/dashboard';
        }

        // Mirrors OrganizationDataService.switchOrganization(), which decides the admin branch
        // from the switch REQUEST params rather than the token. A plain org switch passes no
        // params, and a cross-tab sync is always a plain switch, so the unitId half of that
        // condition is never true here. An admin who also owns a unit still belongs on the org
        // dashboard, which is what the in-tab switch and the login redirect both do.
        if (jwt.admin) {
            return '/app/organization/dashboard';
        }

        if (jwt.unitId) {
            return `/app/unit/detail/${jwt.unitId}`;
        }

        return '/app/owner/dashboard';
    }

    private handleTabVisibilityChange() {
        this._idle.dialogCheck();

        const isLoggedIn: boolean = LegFiJwtService.read() !== null;
        const loggedInRoutes = this._router.url.indexOf('/app/') > -1
                || this._router.url.indexOf('/admin/') > -1
                || this._router.url.indexOf('/pmc/') > -1;
        if (!isLoggedIn && loggedInRoutes) {
            this._growler.error('Signed Out', 'You have been logged out of the app. Please, sign in again to continue.');
            this._authService.logout(true);
        }
    }

    private handleBreakpointObservers() {
        this._observer.observe([BreakpointSizes.SM.observer]).subscribe((res) => {
            this._host.smBreakpoint$.next(res.matches);
        });
        this._observer.observe([BreakpointSizes.MD.observer]).subscribe((res) => {
            this._host.mdBreakpoint$.next(res.matches);
        });
        this._observer.observe([BreakpointSizes.LG.observer]).subscribe((res) => {
            this._host.lgBreakpoint$.next(res.matches);
        });
        this._observer.observe([BreakpointSizes.XL.observer]).subscribe((res) => {
            this._host.xlBreakpoint$.next(res.matches);
        });

        this._observer.observe([BreakpointSizes.LEGACY_SM.observer]).subscribe((res) => {
            this._host.isLegacySmBreakpoint$.next(res.matches);
        });
    }
}
